/** ***********
*
* <app.module.ts v1.0>
*
* Copyright (c) 2023 AIRBUS OPERATIONS
*
* Contributors:
*  PEREIRA Thomas [SOPRA STERIA] [thomas.pereira@soprasteria.com]
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*  ydjoudad [yzaid.djoudad@soprasteria.com]
*
 * All rights reserved.
**************/

import { ContentLoaderService } from './services/shared/content-loader.service';
import { CommonModule, LOCATION_INITIALIZED } from '@angular/common';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { Injector, NgModule, ErrorHandler, inject, provideAppInitializer } from '@angular/core';
import { UntypedFormBuilder, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { JwtHelperService, JWT_OPTIONS } from '@auth0/angular-jwt';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginComponent } from './components/auth/login/login.component';
import { RedirectComponent } from './components/auth/redirect/redirect.component';
import { KpiModule } from './components/kpi/kpi.module';
import { DevToolsModule } from './components/dev-tools/dev-tools.module';
import { HeaderComponent } from './components/shared/header/header.component';
import { ShowConditionsOfUseComponent } from './components/shared/show-conditions-of-use/show-conditions-of-use.component';
import { VideoGuideComponent } from './components/shared/video-guide/video-guide.component';
import { ShowUserNoRightsComponent } from './components/shared/show-user-no-rights/show-user-no-rights.component';
import { SnackbarComponent } from './components/shared/snackbar/snackbar';
import { AppMaterialModule } from './material.modules';
import { TokenInterceptorService } from './services/token-interceptor.service';
import { appEffects, getReducers, REDUCER_TOKEN } from './store';
import { OrderbyModule } from './components/shared/orderby/orderby.module';
import { SharedModule } from './components/shared/shared.module';
import { UserRightModule } from './components/user-right/user-right.module';
import { ProfileCardComponent } from './components/shared/profile-card/profile-card.component';
import { MapScopeManagementService } from './services/map-scope-management.service';
import { CertificatesModule } from './components/certificates/certificates.module';
import { AircraftPlanningModule } from './components/aircraft-planning/aircraft-planning.module';
import { RedirectionInterceptor } from './services/redirection.interceptor';
import { InflatorInterceptor } from './services/inflator.interceptor';
import { DialogConfigureContextComponent } from './components/init-filter/configure-context/dialog-configure-context.component';
import { FilterContextMenuComponent } from './components/shared/filter-context-menu/filter-context-menu.component';
import { DialogManageContextComponent } from './components/init-filter/dialog-manage-context/dialog-manage-context.component';
import { AssetQuantityComponent } from './components/init-filter/configure-context/asset-quantity/asset-quantity.component';
import { DialogOverwriteActionComponent } from './components/init-filter/configure-context/dialog-overwrite-action.component';
import { BehaviorSubject, take } from 'rxjs';
import { CONTEXT_DIALOG_OPENED } from './services/init-filter.service';
import { BannerComponent } from './components/shared/snackbar/banner.component';
import { MobileBottomSheetComponent } from './components/shared/header/mobile-bottom-sheet/mobile-bottom-sheet.component';
import { NotFoundComponent } from './components/not-found/not-found.component';
import { OpenMenuHoverDirective } from './directives/open-menu-hover.directive';
import { QuickSearchFieldComponent } from './components/shared/header/quick-search-field/quick-search-field.component';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { LanguageService } from './services/language.service';

const HttpLoaderFactory = (http: HttpClient) => new TranslateHttpLoader(http, './assets/i18n/', '.json');

const appInitializerFactory = (translate: TranslateService, languageService: LanguageService, injector: Injector) =>
    () => new Promise<void>((resolve) => {
        const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
        locationInitialized.then(() => {
            const supportedLangs = ['en', 'fr', 'de', 'es'];
            translate.addLangs(supportedLangs);
            const browserLang = translate.getBrowserLang();
            const langToSet = browserLang.match(/en|fr|de|es/) ? browserLang : 'en';
            translate.setDefaultLang(langToSet)

            languageService.selectedLanguage$
            .pipe(take(1))
            .subscribe({
                next: (language) => {
                    const lang = supportedLangs.includes(language) ? language : 'en';
                    document.documentElement.setAttribute('lang', lang);

                    translate.use(language).subscribe({
                        next: () => resolve(),
                        error: () => resolve(),
                    });
                },
                error: () => resolve()
            })
        });
    });

@NgModule({
    declarations: [
        AppComponent,
        RedirectComponent,
        HeaderComponent,
        QuickSearchFieldComponent,
        SnackbarComponent,
        LoginComponent,
        ShowConditionsOfUseComponent,
        VideoGuideComponent,
        ShowUserNoRightsComponent,
        ProfileCardComponent,
        DialogConfigureContextComponent,
        FilterContextMenuComponent,
        DialogManageContextComponent,
        AssetQuantityComponent,
        DialogOverwriteActionComponent,
        BannerComponent,
        MobileBottomSheetComponent,
        NotFoundComponent,
        SafeHtmlPipe
    ],
    imports: [
        BrowserModule,
        AppRoutingModule,
        CertificatesModule,
        KpiModule,
        DevToolsModule,
        BrowserAnimationsModule,
        FormsModule,
        ReactiveFormsModule,
        CommonModule,
        OrderbyModule,
        StoreModule.forRoot(REDUCER_TOKEN, {
            runtimeChecks: {
                strictStateImmutability: false,
                strictActionImmutability: false,
                strictStateSerializability: false,
                strictActionSerializability: false,
            },
        }),
        // Uncomment next lines to see redux in chrome (plugin needed)
        // StoreDevtoolsModule.instrument({
        //    maxAge: 25, // Retains last 25 states
        //    logOnly: environment.production, // Restrict extension to log-only mode

        // }),
        EffectsModule.forRoot(appEffects),
        SharedModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            },
            isolate: false
        }),
        AppMaterialModule,
        UserRightModule,
        AircraftPlanningModule,
        OpenMenuHoverDirective,
    ],
    providers: [
        provideHttpClient(withInterceptorsFromDi()),
        provideAppInitializer(() => {
            const initializerFn = appInitializerFactory(
                inject(TranslateService),
                inject(LanguageService),
                inject(Injector)
            );
            return initializerFn();
        }),
        {
            provide: REDUCER_TOKEN,
            useFactory: getReducers
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: InflatorInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: RedirectionInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptorService,
            multi: true,
        },
        { provide: JWT_OPTIONS, useValue: JWT_OPTIONS },
        SnackbarComponent,
        JwtHelperService,
        UntypedFormBuilder,
        ContentLoaderService,
        { provide: ErrorHandler },
        {
            provide: CONTEXT_DIALOG_OPENED,
            useValue: new BehaviorSubject(false),
        },
        MapScopeManagementService
    ],
    bootstrap: [AppComponent]
})
export class AppModule { }
